import { Slot } from '@frontend/slot/types';
import { ExtendedPudoTransaction, TransactionStateName } from '@frontend/transaction/types';

export interface BulkMoveColliUnit {
    key: string;
    transactionId: string;
    transactionState: TransactionStateName;
    trackingNumber: string | null;
    colliId: string | null;
    colliLabel: string | null;
    sourceSpotId: string | null;
    sourceSlotId: string | null;
}

export interface BulkMoveDestination {
    spotId: string;
    moduleId: string;
    slotId: string;
}

export interface BulkMoveAssignment extends BulkMoveColliUnit {
    destination: BulkMoveDestination;
    destinationSlotDisplay: string;
}

/** Expand selected transactions into one move unit per colli (or one unit if no collis). */
export const expandTransactionsToColliUnits = (transactions: ExtendedPudoTransaction[]): BulkMoveColliUnit[] => {
    const units: BulkMoveColliUnit[] = [];

    for (const tx of transactions) {
        const state = tx.state as TransactionStateName;
        const collis = tx.collis?.filter(Boolean) ?? [];

        if (collis.length === 0) {
            units.push({
                key: `${tx.id}:none`,
                transactionId: tx.id,
                transactionState: state,
                trackingNumber: tx.tracking_number ?? null,
                colliId: null,
                colliLabel: null,
                sourceSpotId: tx.spot_id,
                sourceSlotId: tx.slot_id ?? tx.slot?.entity_id ?? null
            });
            continue;
        }

        for (const colli of collis) {
            units.push({
                key: `${tx.id}:${colli.id}`,
                transactionId: tx.id,
                transactionState: state,
                trackingNumber: tx.tracking_number ?? null,
                colliId: colli.id,
                colliLabel: colli.label ?? null,
                sourceSpotId: colli.current_spot_id ?? colli.spot_id ?? tx.spot_id,
                sourceSlotId: colli.current_slot_id ?? colli.slot_id ?? tx.slot_id ?? tx.slot?.entity_id ?? null
            });
        }
    }

    return units;
};

/** Assign every colli unit to the same destination SLOT. */
export const assignAllUnitsToDestinationSlot = (units: BulkMoveColliUnit[], slot: Slot): BulkMoveAssignment[] => {
    const destination: BulkMoveDestination = {
        spotId: slot.spot_id,
        moduleId: slot.module_id,
        slotId: slot.id
    };
    const destinationSlotDisplay = slot.display ?? slot.number ?? slot.id;

    return units.map((unit) => ({
        ...unit,
        destination,
        destinationSlotDisplay
    }));
};
